import { useSeo } from "@/hooks/useSeo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Bell, HandCoins, Handshake, Layers, Megaphone, Package, Rocket, ShoppingCart, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError, GAME_STYLES } from "@/lib/api";
import { useAutoRefresh } from "@/lib/live";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import Hero from "@/components/Hero";
import Manifesto from "@/components/Manifesto";
import ProductCard from "@/components/ProductCard";

// Conto alla rovescia live verso l'uscita di un set (si aggiorna ogni 30s)
function SetCountdown({ date, testid }) {
  const target = new Date(`${date}T09:00:00`).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const diff = target - now;
  if (diff <= 0) return <span data-testid={testid} className="font-mono text-xs font-bold text-red-400 uppercase tracking-widest">In arrivo oggi</span>;
  const g = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-2 font-mono text-xs font-bold text-red-400 uppercase tracking-widest">
      {g > 0 && <span>{g} <span className="text-zinc-500 font-normal">giorni</span></span>}
      <span>{h} <span className="text-zinc-500 font-normal">ore</span></span>
      <span>{m} <span className="text-zinc-500 font-normal">min</span></span>
    </span>
  );
}

// "Avvisami" su un set in uscita: email (prefill se loggato) → avviso automatico all'apertura preorder
function SetAlertForm({ setId, testid }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  const submit = async () => {
    if (!email.trim()) return toast.error("Inserisci la tua email");
    setBusy(true);
    try {
      const { data } = await api.post(`/upcoming-sets/${setId}/notify`, { email: email.trim() });
      toast.success(data.message);
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p data-testid={`${testid}-done`} className="text-[11px] font-mono text-emerald-400 flex items-center gap-1.5">
        <Bell size={12} /> Campanella attiva: ti avvisiamo all'apertura dei preorder
      </p>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        data-testid={testid}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="La tua email"
        className="flex-1 min-w-0 bg-black/60 border border-white/10 focus:border-red-600/70 rounded-full px-3.5 py-2 text-xs text-white placeholder:text-zinc-600 outline-none transition-colors"
      />
      <button data-testid={`${testid}-submit`} onClick={submit} disabled={busy} title="Ti avvisiamo via email (e in campanella se hai un account) quando aprono i preorder" className="inline-flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-wide text-zinc-300 border border-white/15 hover:border-red-600/60 hover:text-white rounded-full px-3.5 py-2 transition-all active:scale-[0.97] disabled:opacity-40 shrink-0">
        <Bell size={11} /> Avvisami
      </button>
    </div>
  );
}

export default function Home() {
  useSeo({ title: "Negozio carte Pokémon, Magic e One Piece a Torino | WANTED", description: "WANTED è il negozio di carte collezionabili a Torino Lingotto: singole, graded, sealed e tornei. Compra e vendi carte, anche online con ritiro in store.", path: "/" });
  const [featured, setFeatured] = useState([]);
  const [vetrina, setVetrina] = useState([]);
  const [topWanted, setTopWanted] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingGame, setUpcomingGame] = useState("Tutti");
  const [liveTournament, setLiveTournament] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [bundleTiers, setBundleTiers] = useState([]);
  const { add } = useCart();

  const load = () => {
    // Prima i prodotti segnati "In prima pagina" dall'admin (max 4); se nessuno, gli ultimi arrivi
    api.get("/products", { params: { featured: 1, limit: 4 } })
      .then((r) => {
        if (r.data.length > 0) setFeatured(r.data.slice(0, 4));
        else return api.get("/products", { params: { limit: 4 } }).then((r2) => setFeatured(r2.data.slice(0, 4)));
      })
      .catch(() => {});
    api.get("/consignment/vetrina").then((r) => setVetrina(r.data.slice(0, 3))).catch(() => {});
    // Le 3 carte della buylist che paghiamo di più (sezione nascosta se la buylist è vuota)
    api.get("/wanted", { params: { sort: "prezzo_desc" } }).then((r) => setTopWanted((r.data.items || []).slice(0, 3))).catch(() => {});
    // Set in uscita con countdown (vetrina gestita dall'admin; vuota = sezione nascosta)
    api.get("/upcoming-sets").then((r) => setUpcoming(r.data)).catch(() => {});
    // Bundle in evidenza scelto dall'admin (null = sezione nascosta)
    api.get("/featured-bundle").then((r) => setBundle(r.data?.bundle || null)).catch(() => {});
    // Fasce "Crea il tuo bundle": in home si pubblicizza la più vantaggiosa
    api.get("/bundle-config").then((r) => setBundleTiers(r.data?.tiers || [])).catch(() => {});
    // Torneo in corso in questo momento in negozio
    api.get("/tournaments").then((r) => {
      setLiveTournament(r.data.find((t) => t.registration_state === "in_corso") || null);
    }).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);
  useAutoRefresh(load, 60000); // prima pagina e vetrina si aggiornano senza reload

  // Il bundle entra nel carrello tutto insieme: lo sconto di gruppo scatta al checkout
  const addBundleToCart = () => {
    if (!bundle?.available) return;
    bundle.items.forEach((p) => add(p, 1));
    toast.success(`Bundle "${bundle.name}" nel carrello: sconto ${bundle.discount}% applicato al checkout`);
  };

  return (
    <main>
      <Hero />

      {liveTournament && (
        <motion.section
          data-testid="live-tournament-banner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-7xl mx-auto px-4 sm:px-8 mt-10"
        >
          <Link to="/tornei" className="block slab-frame rounded-2xl p-5 sm:p-6 border-red-600/40 hover:border-red-600/70 transition-colors group">
            <div className="flex flex-wrap items-center gap-4">
              <span className="relative flex h-3.5 w-3.5 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" aria-hidden />
                <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500" />
              </span>
              <div className="flex-1 min-w-52">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-red-500 font-bold">Ora in negozio — Live</p>
                <p className="font-display font-bold text-white text-lg uppercase tracking-tight mt-1 truncate">
                  {liveTournament.name} <span className="text-zinc-500 font-normal normal-case">({liveTournament.game})</span>
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span data-testid="live-tournament-players" className="font-mono text-sm text-zinc-300">
                  <span className="text-white font-bold">{liveTournament.participants_count}</span>/{liveTournament.max_players} giocatori al tavolo
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wide text-red-400 group-hover:text-red-300 transition-colors">
                  Segui il torneo <ArrowRight size={13} />
                </span>
              </div>
            </div>
          </Link>
        </motion.section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-red-500 font-semibold mb-3">Fresh drop</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold uppercase tracking-tight text-white">
              In prima pagina
            </h2>
          </div>
          <Link to="/catalogo" data-testid="home-view-all-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-display font-bold uppercase tracking-wide text-zinc-400 hover:text-red-500 transition-colors">
            Vedi tutto <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="home-featured-grid">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {bundle && (
        <section data-testid="home-bundle" className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative slab-frame rounded-2xl overflow-hidden border-red-600/30"
          >
            <div className="absolute -top-24 -left-24 w-80 h-80 bg-red-600/15 blur-[110px] rounded-full" aria-hidden />
            <div className="relative grid lg:grid-cols-5 gap-10 p-8 sm:p-12">
              <div className="lg:col-span-3">
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-red-500 font-semibold mb-3 flex items-center gap-2">
                  <Package size={14} /> Offerta combinata
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold uppercase tracking-tight text-white">
                  Bundle {bundle.name}
                </h2>
                <p className="text-sm text-zinc-400 mt-3 max-w-lg leading-relaxed">
                  Tutti i pezzi del gruppo in un colpo solo: lo sconto del {bundle.discount}% scatta in automatico nel carrello.
                </p>
                <ul className="mt-7 space-y-3">
                  {bundle.items.map((p) => (
                    <li key={p.id} data-testid={`home-bundle-item-${p.id}`} className="flex items-center gap-4 bg-[#050505]/60 border border-white/10 rounded-xl px-4 py-3">
                      <div className="w-11 h-11 rounded-lg bg-[#0D0D0E] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                        {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-contain" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono font-bold text-sm text-white truncate">{p.name}</p>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{p.game || p.category}{p.grade ? ` • ${p.grading} ${p.grade}` : ""}</p>
                      </div>
                      <span className="font-mono text-sm text-zinc-300 shrink-0">{fmt(p.final_price)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-2 flex flex-col justify-center">
                <div className="bg-[#050505]/60 border border-red-600/25 rounded-2xl p-7">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border text-red-400 border-red-500/40 bg-red-500/10">
                    Bundle -{bundle.discount}%
                  </span>
                  <p className="font-mono text-sm text-zinc-500 line-through mt-5">{fmt(bundle.full_total)}</p>
                  <p data-testid="home-bundle-price" className="font-display text-4xl font-extrabold text-white mt-1">{fmt(bundle.discounted_total)}</p>
                  <p className="text-xs font-mono text-emerald-400 mt-2">Risparmi {fmt(bundle.savings)}</p>
                  <button
                    data-testid="home-bundle-add"
                    onClick={addBundleToCart}
                    disabled={!bundle.available}
                    className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full mt-6 transition-all active:scale-[0.98] hover:shadow-[0_0_30px_rgba(220,38,38,0.4)]"
                  >
                    <ShoppingCart size={16} /> {bundle.available ? "Aggiungi tutto al carrello" : "Temporaneamente esaurito"}
                  </button>
                  <p className="text-[10px] font-mono text-zinc-600 text-center mt-3">Lo sconto si applica solo comprando tutto il gruppo</p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {bundleTiers.length > 0 && (
        <section data-testid="home-build-bundle" className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative slab-frame rounded-2xl overflow-hidden border-emerald-600/25 p-8 sm:p-12"
          >
            <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-emerald-600/10 blur-[110px] rounded-full" aria-hidden />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex-1">
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                  <Layers size={14} /> Lo sconto lo componi tu
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold uppercase tracking-tight text-white">
                  Crea il tuo bundle
                </h2>
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-400 leading-loose mt-3 max-w-lg">
                  Scegli le carte tra gli articoli idonei e guarda lo sconto crescere col totale
                </p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {bundleTiers.map((t) => (
                    <span key={t.min_total} className="text-[10px] font-mono uppercase tracking-widest font-bold rounded-full px-3.5 py-1.5 border text-emerald-300/90 border-emerald-500/30 bg-emerald-500/5">
                      da {fmt(t.min_total)} → -{t.percent}%
                    </span>
                  ))}
                </div>
              </div>
              <Link
                to="/bundle"
                data-testid="home-build-bundle-cta"
                className="shrink-0 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full transition-all active:scale-[0.98] hover:shadow-[0_0_30px_rgba(16,185,129,0.35)]"
              >
                <Layers size={16} /> Componi il tuo bundle
              </Link>
            </div>
          </motion.div>
        </section>
      )}

      <Manifesto />

      <section data-testid="home-creator" className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative slab-frame rounded-2xl overflow-hidden border-red-600/25 p-8 sm:p-12"
        >
          <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-red-600/10 blur-[110px] rounded-full" aria-hidden />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
            <img src="/mascotte.png" alt="" aria-hidden className="absolute -bottom-6 right-4 w-32 opacity-90 rotate-6 pointer-events-none select-none hidden sm:block" draggable={false} />
            <div className="flex-1">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-red-500 font-semibold mb-3 flex items-center gap-2">
                <Megaphone size={14} /> Programma creator
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold uppercase tracking-tight text-white">
                Fai contenuti TCG? Lavora con noi
              </h2>
              <p className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-400 leading-loose mt-3 max-w-lg">
                Condividi il tuo link personale: per ogni acquisto che porti guadagni credito Wanted
              </p>
            </div>
            <Link
              to="/diventa-creator"
              data-testid="home-creator-cta"
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full transition-all active:scale-[0.98] hover:shadow-[0_0_30px_rgba(220,38,38,0.35)] sm:mr-28"
            >
              <Megaphone size={16} /> Diventa creator
            </Link>
          </div>
        </motion.div>
      </section>

      {vetrina.length > 0 && (
        <section data-testid="home-vetrina" className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
          <div className="border border-amber-500/20 bg-amber-500/[0.04] rounded-2xl p-8 sm:p-12">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 font-semibold mb-3 flex items-center gap-2">
                  <Handshake size={14} /> Pezzi unici dei collezionisti
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold uppercase tracking-tight text-white">
                  Vetrina conto vendita
                </h2>
                <p className="text-sm text-zinc-400 mt-3 max-w-xl leading-relaxed">
                  Carte e sealed affidati a Wanted dalla community: usato selezionato, pezzi unici, prezzi concordati. Quando sono venduti, sono andati.
                </p>
              </div>
              <Link to="/catalogo?macro=contovendita" data-testid="home-vetrina-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-display font-bold uppercase tracking-wide text-amber-400 hover:text-amber-300 transition-colors shrink-0">
                Tutta la vetrina <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {vetrina.map((i, idx) => (
                <motion.div key={i.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: idx * 0.08 }}>
                  <Link to={`/vetrina/${i.id}`} data-testid={`home-vetrina-card-${i.id}`} className="slab-frame rounded-2xl overflow-hidden group block hover:border-amber-500/40 transition-colors">
                    <div className="aspect-[4/3] bg-[#0D0D0E] border-b border-white/10 overflow-hidden flex items-center justify-center">
                      {i.image ? (
                        <img src={i.image} alt={i.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <Handshake size={28} className="text-zinc-700" />
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className="text-[9px] font-mono uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border text-amber-400 border-amber-500/30 bg-amber-500/10">Conto vendita</span>
                        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">Pezzo unico</span>
                      </div>
                      <h3 className="font-display text-base font-bold uppercase tracking-tight text-white group-hover:text-amber-300 transition-colors leading-snug">{i.name}</h3>
                      <p className="font-mono font-bold text-lg text-amber-400 mt-2.5">{fmt(i.agreed_price)}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {topWanted.length > 0 && (
        <section data-testid="home-wanted" className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
          <div className="border border-emerald-500/20 bg-emerald-500/[0.04] rounded-2xl p-8 sm:p-12">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                  <HandCoins size={14} /> Buylist Wanted
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold uppercase tracking-tight text-white">
                  Ce l'hai? Lo vendi? Ti offriamo questo prezzo
                </h2>
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-400 leading-loose mt-3 max-w-xl">
                  In questa sezione abbiamo inserito i prodotti che stiamo cercando maggiormente in questo momento: se ce l'hai portalo nel nostro negozio e noi lo acquisteremo pagandoti in denaro o in credito Wanted maggiorato.
                </p>
              </div>
              <Link to="/wanted" data-testid="home-wanted-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-display font-bold uppercase tracking-wide text-emerald-400 hover:text-emerald-300 transition-colors shrink-0">
                Tutta la buylist <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {topWanted.map((w, idx) => (
                <motion.div key={w.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: idx * 0.08 }}>
                  <Link to={`/wanted/${w.id}`} data-testid={`home-wanted-card-${w.id}`} className="slab-frame rounded-2xl overflow-hidden group block hover:border-emerald-500/40 transition-colors">
                    <div className="aspect-square bg-black border-b border-white/10 overflow-hidden flex items-center justify-center relative">
                      {w.image_url ? (
                        <img src={w.image_url} alt={w.name} loading="lazy" className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <Package size={28} className="text-zinc-700" />
                      )}
                      <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-wrap justify-end gap-1 sm:gap-1.5 max-w-[65%] sm:max-w-[70%] z-10">
                        <span className={`text-[8px] sm:text-[9px] font-mono uppercase tracking-widest font-bold px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded leading-tight ${GAME_STYLES[w.game] || "bg-zinc-700 text-white"}`}>{w.game}</span>
                        {w.graded && (
                          <span className="text-[8px] sm:text-[9px] font-mono uppercase tracking-widest font-bold bg-cyan-500 text-white rounded px-1.5 py-0.5 sm:px-2.5 sm:py-1 leading-tight">{w.grading_company} {w.grade}</span>
                        )}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-base font-bold uppercase tracking-tight text-white group-hover:text-emerald-300 transition-colors leading-snug">{w.name}</h3>
                      <p className="font-mono font-bold text-lg text-emerald-400 mt-2.5">
                        {w.price_display ? (w.price_display.range ? `Fino a ${fmt(w.price_display.max)}` : fmt(w.price_display.max)) : ""} <span className="text-xs text-zinc-500 font-normal">a copia</span>
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
            <Link to="/wanted" data-testid="home-wanted-link-mobile" className="sm:hidden mt-8 inline-flex items-center gap-2 text-sm font-display font-bold uppercase tracking-wide text-emerald-400">
              Tutta la buylist <ArrowRight size={15} />
            </Link>
          </div>
        </section>
      )}

      {upcoming.length > 0 && (
        <section data-testid="home-upcoming" className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
          <div className="border border-red-600/20 bg-red-600/[0.04] rounded-2xl p-8 sm:p-12">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-red-500 font-semibold mb-3 flex items-center gap-2">
                  <Rocket size={14} /> Prossime uscite
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold uppercase tracking-tight text-white">
                  I set stanno arrivando
                </h2>
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-400 leading-loose mt-3 max-w-xl">
                  Il countdown è partito: preordina subito il tuo box.
                </p>
              </div>
            </div>
            {[...new Set(upcoming.map((s) => s.game).filter(Boolean))].length > 1 && (
              <div className="flex flex-wrap gap-2 mb-8" data-testid="home-upcoming-games">
                {["Tutti", ...new Set(upcoming.map((s) => s.game).filter(Boolean))].map((g) => (
                  <button key={g} data-testid={`home-upcoming-game-${g === "Tutti" ? "tutti" : g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setUpcomingGame(g)} className={`text-[10px] font-mono uppercase tracking-widest px-3.5 py-2 rounded-full border transition-all active:scale-[0.96] ${upcomingGame === g ? "bg-red-600 border-red-600 text-white" : "border-white/15 text-zinc-400 hover:text-white hover:border-red-600/50"}`}>
                    {g}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {upcoming.filter((s) => upcomingGame === "Tutti" || s.game === upcomingGame).map((s, idx) => {
                const todayStr = new Date().toLocaleDateString("sv-SE");
                const preorderOpen = !s.preorder_date || s.preorder_date <= todayStr;
                return (
                <motion.div key={s.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: idx * 0.08 }}>
                  <div data-testid={`home-upcoming-card-${s.id}`} className="slab-frame rounded-2xl overflow-hidden hover:border-red-600/40 transition-colors h-full flex flex-col">
                    {/* Su telefono la card è orizzontale: foto a sinistra, testo a destra */}
                    <div className="flex sm:block">
                      <div className="w-28 shrink-0 sm:w-full sm:aspect-square bg-black border-b-0 sm:border-b border-r sm:border-r-0 border-white/10 overflow-hidden flex items-center justify-center relative">
                        {s.image_url ? (
                          <img src={s.image_url} alt={s.name} loading="lazy" className="w-full h-full object-contain p-2 sm:p-4" />
                        ) : (
                          <Rocket size={28} className="text-zinc-700" />
                        )}
                        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-wrap justify-end gap-1 sm:gap-1.5 max-w-[65%] sm:max-w-[70%] z-10">
                          <span className={`text-[8px] sm:text-[9px] font-mono uppercase tracking-widest font-bold px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded leading-tight ${GAME_STYLES[s.game] || "bg-zinc-700 text-white"}`}>{s.game}</span>
                        </div>
                      </div>
                      <div className="p-4 sm:p-5 min-w-0 flex-1">
                        <h3 className="font-display text-sm sm:text-base font-bold uppercase tracking-tight text-white leading-snug">{s.name}</h3>
                        <p className="text-[11px] sm:text-xs font-mono text-zinc-500 mt-1.5">Esce il {new Date(`${s.release_date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</p>
                        {s.preorder_date && (
                          <p data-testid={`home-upcoming-preorder-date-${s.id}`} className="text-[11px] sm:text-xs font-mono text-amber-400/90 mt-1">
                            Preorder dal {new Date(`${s.preorder_date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}
                          </p>
                        )}
                        {s.note && <p className="hidden sm:block text-xs text-zinc-400 mt-2 leading-relaxed">{s.note}</p>}
                      </div>
                    </div>
                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex flex-col gap-3 flex-1 justify-end">
                      <div className="flex items-center justify-between gap-3">
                        <SetCountdown date={s.release_date} testid={`home-upcoming-countdown-${s.id}`} />
                        {preorderOpen ? (
                          <Link to={s.product_id ? `/prodotto/${s.product_id}` : "/contatti"} data-testid={`home-upcoming-preorder-${s.id}`} className="inline-flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wide text-white bg-red-600 hover:bg-red-700 rounded-full px-4 py-2 transition-all active:scale-[0.97] shrink-0">
                            {s.product_id ? "Preordina" : "Prenota"} <ArrowRight size={12} />
                          </Link>
                        ) : (
                          <span data-testid={`home-upcoming-preorder-${s.id}`} title={`I preorder aprono il ${new Date(`${s.preorder_date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}`} className="inline-flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wide text-zinc-600 border border-white/10 rounded-full px-4 py-2 cursor-not-allowed select-none shrink-0">
                            Presto <ArrowRight size={12} />
                          </span>
                        )}
                      </div>
                      <SetAlertForm setId={s.id} testid={`home-upcoming-alert-${s.id}`} />
                    </div>
                  </div>
                </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

    </main>
  );
}
